/**
 * TenancyCancelDialog — the cancellation gesture for a tenancy, with the
 * read-only preflight in front of it (Slice 3).
 *
 * Until now this dialog was ~50 lines inline in TenancyCard.js: a reason box, an
 * optional no-show checkbox, confirm. It asked for a decision without showing
 * what the decision touches. The backend has answered that question since Slice
 * 1 — GET .../cancellation-preflight returns the lifecycle cancelability, every
 * local invoice with its stored AND effective status, the linked Bexio
 * artefacts, move-in evidence and a contract count — and nothing was reading it.
 * This dialog reads it.
 *
 * WHAT THIS DIALOG DOES NOT DO — three deliberate omissions, each of which
 * would look like an improvement:
 *
 *   1. It does not decide cancelability. `can_cancel`,
 *      `requires_no_show_confirmation` and `block_reason` come from the
 *      preflight verbatim. Re-deriving any of them here from status, move-in
 *      date or termination fields would be a second cancelability matrix that
 *      can disagree with the first, and the disagreement would always be
 *      resolved in favour of the one that cannot see the database. Same
 *      reasoning as InvoiceCancelDialog.
 *
 *   2. It cancels nothing except the tenancy. An unpaid invoice is shown, and
 *      linked to, so a human can go and cancel it through the dedicated safe
 *      path. Whether a tenancy cancellation SHOULD cascade into its invoices is
 *      a business decision nobody has taken; the answer is not "whatever the
 *      dialog happens to do".
 *
 *   3. Move-in evidence warns but never blocks. A finalized protocol or a
 *      signed key receipt is a strong reason for a human to stop and think, and
 *      it is not today a reason the backend refuses the write. A frontend-only
 *      hard guard would be exactly the second matrix from (1). That guard
 *      belongs in `derive_tenancy_cancelability`, where the preflight and the
 *      write would both inherit it.
 *
 * The preflight is a REVIEW aid, never a security boundary: `cancel_tenancy`
 * re-derives cancelability server-side on the row it is about to write, so a
 * preflight that went stale between the two calls loses to the server. A 409
 * after a green preflight is therefore a normal outcome, not a bug — it is
 * shown as a refusal and the dialog stays open.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../components/ui/accordion";
import {
  cancelAdminTenancy,
  fetchAdminTenancyCancellationPreflight,
} from "../../../api/adminData";
import { formatDateOnly } from "../../../utils/datetime";

export const CANCEL_REASON_MIN = 3;
export const CANCEL_REASON_MAX = 500;

/**
 * The backend's block codes, translated. The CODES are the contract; the German
 * sentences the server also sends are its own to reword, so nothing here
 * matches on them. An unknown code falls through to the code itself rather than
 * to a reassuring generic sentence — a reason we cannot name is still a reason
 * the admin has to see.
 */
const BLOCK_REASON_TEXT = {
  already_cancelled: "Das Mietverhältnis ist bereits storniert.",
  already_ended: "Das Mietverhältnis ist bereits beendet.",
  already_terminated:
    "Für dieses Mietverhältnis wurde bereits eine Kündigung oder ein Austritt erfasst.",
  missing_move_in_date: "Es ist kein Mietbeginn erfasst.",
  past_no_show_grace:
    "Das Mietverhältnis hat bereits begonnen. Bitte eine Kündigung oder einen Austritt erfassen.",
  // Slice 4. Names the ARTEFACT, not the rule: an admin who reads
  // "Einzugsprotokoll oder unterschriebene Schlüsselquittung" knows which
  // document to go and look at — and the Einzugsnachweise section below is
  // already open, because a block of this kind implies a document to show.
  move_in_evidence_present:
    "Für dieses Mietverhältnis wurde ein Einzug dokumentiert (Einzugsprotokoll oder unterschriebene Schlüsselquittung). Bitte eine Kündigung oder einen Austritt erfassen.",
};

const EFFECTIVE_STATUS_TEXT = {
  unpaid: "offen",
  overdue: "überfällig",
  paid: "bezahlt",
  cancelled: "storniert",
};

function formatMoney(amount, currency) {
  if (amount === null || amount === undefined || amount === "") return "—";
  return `${currency ? `${currency} ` : ""}${amount}`;
}

/** `amount` is a JSON number on local invoices, a STRING on Bexio mirrors
 *  (NUMERIC(12,2) — money must not round-trip through a binary float). Only the
 *  local one is formatted to two decimals; the Bexio string is passed through
 *  untouched. */
function formatLocalAmount(amount, currency) {
  if (typeof amount !== "number" || Number.isNaN(amount)) {
    return formatMoney(amount, currency);
  }
  return `${currency ? `${currency} ` : ""}${amount.toFixed(2)}`;
}

function Section({ value, title, count, warn, children }) {
  return (
    <AccordionItem value={value} className="border-b border-border last:border-b-0">
      <AccordionTrigger className="px-1 py-3 text-[13px] font-semibold hover:no-underline">
        <span className="flex items-center gap-2 text-left">
          {warn ? <span aria-hidden="true">⚠</span> : null}
          {title}
          {typeof count === "number" ? (
            <span className="text-muted-foreground">({count})</span>
          ) : null}
        </span>
      </AccordionTrigger>
      <AccordionContent className="px-1 pb-3 text-[12.5px]">
        {children}
      </AccordionContent>
    </AccordionItem>
  );
}

export default function TenancyCancelDialog({ tenancy, onClose, onCancelled }) {
  const [preflight, setPreflight] = useState(null);
  const [preflightError, setPreflightError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [noShowConfirmed, setNoShowConfirmed] = useState(false);
  const [pending, setPending] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const tenancyId = tenancy?.id;

  /**
   * One fetch per open, and a fresh one every time. Keying on the id means a
   * close (id -> undefined) and a reopen (undefined -> id) re-runs this, which
   * is the point: a fact sheet that was true when the dialog was last closed is
   * not evidence about now. `stale` drops a response whose dialog has already
   * moved on, so a slow request cannot repopulate a closed or retargeted
   * dialog.
   */
  useEffect(() => {
    setReason("");
    setNoShowConfirmed(false);
    setSubmitError(null);
    setPreflight(null);
    setPreflightError(null);

    if (!tenancyId) {
      setLoading(false);
      return undefined;
    }

    let stale = false;
    setLoading(true);
    fetchAdminTenancyCancellationPreflight(tenancyId)
      .then((data) => {
        if (stale) return;
        setPreflight(data);
      })
      .catch((err) => {
        if (stale) return;
        setPreflightError(
          err?.message || "Die Stornierung konnte nicht geprüft werden."
        );
      })
      .finally(() => {
        if (!stale) setLoading(false);
      });

    return () => {
      stale = true;
    };
  }, [tenancyId]);

  const cancelability = preflight?.lifecycle_cancelability;
  const canCancel = cancelability?.can_cancel === true;
  const requiresNoShow = cancelability?.requires_no_show_confirmation === true;
  const blockReason = cancelability?.block_reason || null;

  const trimmed = reason.trim();
  const tooShort = trimmed.length < CANCEL_REASON_MIN;
  const tooLong = trimmed.length > CANCEL_REASON_MAX;

  /**
   * Everything in this gate is either the server's verdict or the reason field.
   * Move-in evidence, invoice counts, Bexio facts and contract counts are
   * deliberately NOT in it — see omission (3) in the file docstring.
   */
  const canSubmit =
    Boolean(tenancyId) &&
    Boolean(preflight) &&
    canCancel &&
    !tooShort &&
    !tooLong &&
    (!requiresNoShow || noShowConfirmed) &&
    !pending;

  const handleConfirm = useCallback(async () => {
    if (!canSubmit) return;
    setPending(true);
    setSubmitError(null);
    try {
      await cancelAdminTenancy(tenancyId, trimmed, requiresNoShow && noShowConfirmed);
      toast.success("Mietverhältnis storniert.");
      onCancelled?.();
    } catch (err) {
      // The server's sentence, whatever the code was. The dialog stays open so a
      // refusal is visibly a refusal and never a silent success.
      const message =
        err?.message || "Das Mietverhältnis konnte nicht storniert werden.";
      setSubmitError(message);
      toast.error(`Stornierung fehlgeschlagen: ${message}`);
    } finally {
      setPending(false);
    }
  }, [
    canSubmit,
    tenancyId,
    trimmed,
    requiresNoShow,
    noShowConfirmed,
    onCancelled,
  ]);

  const invoices = preflight?.local_invoices;
  const drafts = preflight?.bexio?.draft_requests;
  const bexioInvoices = preflight?.bexio?.invoices;
  const evidence = preflight?.move_in_evidence;
  const contracts = preflight?.contracts;
  const hasEvidence =
    evidence?.finalized_move_in_inspection === true ||
    evidence?.signed_key_receipt === true;

  return (
    <AlertDialog
      open={Boolean(tenancy)}
      onOpenChange={(open) => {
        if (!open && !pending) onClose?.();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Mietverhältnis stornieren</AlertDialogTitle>
          <AlertDialogDescription>
            Bitte prüfe die Fakten unten und gib einen Stornogrund ein. Dieser
            wird im Audit-Log gespeichert.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Scrollable review area. The reason field and the footer stay outside
            it so the confirm button is reachable on a phone no matter how many
            invoices the tenancy has. Same shape as KeyReceiptModal and
            InspectionChecklistModal. */}
        <div className="max-h-[45vh] overflow-y-auto pr-1">
          {loading ? (
            <p className="m-0 py-2 text-[13px] text-muted-foreground">
              Stornierung wird geprüft …
            </p>
          ) : null}

          {preflightError ? (
            <p
              role="alert"
              className="m-0 rounded-[8px] border border-destructive/30 bg-destructive/5 px-3 py-2 text-[12.5px] text-destructive"
            >
              Die Stornierung konnte nicht geprüft werden: {preflightError}
            </p>
          ) : null}

          {preflight ? (
            <>
              <div className="mb-3">
                <p className="m-0 text-[12px] text-muted-foreground">
                  {preflight.display_status || preflight.status}
                  {preflight.move_in_date
                    ? ` · Einzug ${formatDateOnly(preflight.move_in_date)}`
                    : ""}
                  {preflight.as_of_date
                    ? ` · Stand ${formatDateOnly(preflight.as_of_date)}`
                    : ""}
                </p>
                {canCancel ? (
                  <p className="m-0 mt-1 text-[13px] font-semibold text-emerald-700 dark:text-emerald-400">
                    Stornierung ist grundsätzlich möglich.
                  </p>
                ) : (
                  <p
                    role="alert"
                    className="m-0 mt-1 text-[13px] font-semibold text-destructive"
                  >
                    Stornierung ist aktuell nicht möglich.
                    {blockReason
                      ? ` ${BLOCK_REASON_TEXT[blockReason] || blockReason}`
                      : ""}
                  </p>
                )}
              </div>

              <Accordion type="multiple" defaultValue={hasEvidence ? ["evidence"] : []}>
                <Section
                  value="invoices"
                  title="Lokale Rechnungen"
                  count={invoices?.total ?? 0}
                >
                  {invoices?.total ? (
                    <>
                      <p className="m-0 mb-2 text-[11.5px] text-muted-foreground">
                        {invoices.unpaid} offen · {invoices.overdue} überfällig ·{" "}
                        {invoices.paid} bezahlt · {invoices.cancelled} storniert
                      </p>
                      <ul className="m-0 list-none space-y-2 p-0">
                        {invoices.items.map((inv) => (
                          <li
                            key={inv.id}
                            className="rounded-[8px] border border-border px-2.5 py-2"
                          >
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                              <span className="font-medium">
                                {inv.invoice_number || `#${inv.id}`}
                              </span>
                              <span>
                                {formatLocalAmount(inv.amount, inv.currency)}
                              </span>
                              <span className="text-muted-foreground">
                                {EFFECTIVE_STATUS_TEXT[inv.effective_status] ||
                                  inv.effective_status}
                              </span>
                              {inv.stored_status &&
                              inv.stored_status !== inv.effective_status ? (
                                <span className="text-[11px] text-muted-foreground">
                                  (gespeichert: {inv.stored_status})
                                </span>
                              ) : null}
                            </div>
                            <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[11.5px] text-muted-foreground">
                              {inv.due_date ? (
                                <span>fällig {formatDateOnly(inv.due_date)}</span>
                              ) : null}
                              {inv.paid_at ? (
                                <span>bezahlt {formatDateOnly(inv.paid_at)}</span>
                              ) : null}
                              <Link
                                to={`/admin/invoices/${inv.id}`}
                                className="font-medium text-primary underline-offset-2 hover:underline"
                              >
                                Rechnung öffnen
                              </Link>
                            </div>
                          </li>
                        ))}
                      </ul>
                      {invoices.unpaid + invoices.overdue > 0 ? (
                        <p className="m-0 mt-2 text-[11.5px] text-muted-foreground">
                          Offene Rechnung bitte separat prüfen. Sie wird durch
                          diese Stornierung nicht verändert.
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <p className="m-0 text-muted-foreground">
                      Keine lokalen Rechnungen.
                    </p>
                  )}
                </Section>

                <Section
                  value="drafts"
                  title="Bexio-Entwürfe"
                  count={drafts?.total ?? 0}
                >
                  {drafts?.total ? (
                    <ul className="m-0 list-none space-y-2 p-0">
                      {drafts.items.map((d) => (
                        <li
                          key={d.id}
                          className="rounded-[8px] border border-border px-2.5 py-2"
                        >
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            {d.needs_attention ? (
                              <span aria-label="Prüfen">⚠</span>
                            ) : null}
                            <span className="font-medium">
                              {d.document_nr || `#${d.id}`}
                            </span>
                            {d.rent_period ? (
                              <span className="text-muted-foreground">
                                {formatDateOnly(d.rent_period)}
                              </span>
                            ) : null}
                            <span className="text-muted-foreground">
                              {d.status}
                            </span>
                          </div>
                          {/* Display copy from the ledger. Shown, never branched on. */}
                          {d.status_hint ? (
                            <p className="m-0 mt-0.5 text-[11.5px] text-muted-foreground">
                              {d.status_hint}
                            </p>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="m-0 text-muted-foreground">
                      Keine Bexio-Entwürfe.
                    </p>
                  )}
                </Section>

                <Section
                  value="bexio-invoices"
                  title="Bexio-Rechnungen"
                  count={bexioInvoices?.total ?? 0}
                >
                  {bexioInvoices?.total ? (
                    <ul className="m-0 list-none space-y-2 p-0">
                      {bexioInvoices.items.map((b) => (
                        <li
                          key={b.id}
                          className="rounded-[8px] border border-border px-2.5 py-2"
                        >
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                            <span className="font-medium">
                              {b.document_nr || `#${b.bexio_invoice_id}`}
                            </span>
                            {/* String, not float: NUMERIC(12,2) stays exact. */}
                            <span>{formatMoney(b.total, b.currency)}</span>
                            <span className="text-muted-foreground">
                              {b.bexio_status}
                            </span>
                          </div>
                          {b.due_date ? (
                            <p className="m-0 mt-0.5 text-[11.5px] text-muted-foreground">
                              fällig {formatDateOnly(b.due_date)}
                            </p>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="m-0 text-muted-foreground">
                      Keine verknüpften Bexio-Rechnungen.
                    </p>
                  )}
                  {preflight.bexio?.coverage === "linked_only" ? (
                    <p className="m-0 mt-2 text-[11.5px] text-muted-foreground">
                      Es werden nur verknüpfte Bexio-Belege angezeigt. Weitere
                      Belege in Bexio sind damit nicht ausgeschlossen.
                    </p>
                  ) : null}
                </Section>

                <Section
                  value="evidence"
                  title="Einzugsnachweise"
                  warn={hasEvidence}
                >
                  <ul className="m-0 list-none space-y-1 p-0">
                    <li>
                      Einzugsprotokolle: {evidence?.move_in_inspection_count ?? 0}
                      {evidence?.finalized_move_in_inspection
                        ? ` · finalisiert${
                            evidence.latest_finalized_at
                              ? ` am ${formatDateOnly(evidence.latest_finalized_at)}`
                              : ""
                          }`
                        : " · keines finalisiert"}
                    </li>
                    <li>
                      Schlüsselquittungen: {evidence?.key_receipt_count ?? 0}
                      {evidence?.signed_key_receipt
                        ? ` · unterschrieben${
                            evidence.latest_signed_at
                              ? ` am ${formatDateOnly(evidence.latest_signed_at)}`
                              : ""
                          }`
                        : " · keine unterschrieben"}
                    </li>
                  </ul>
                  {/* Deliberately says nothing about whether cancelling is
                      allowed. `hasEvidence` is a DISPLAY fact and is true for a
                      protocol finalized before move-in too — where the server
                      still permits the cancellation. A warning that judged the
                      case here would be a second cancelability rule living in
                      the browser, and it would be wrong in exactly the no-show
                      situation this dialog exists for. So it flags the document
                      and points at the one verdict, above. */}
                  {hasEvidence ? (
                    <p
                      role="alert"
                      className="m-0 mt-2 rounded-[8px] border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-[12px] text-amber-800 dark:text-amber-200"
                    >
                      Einzugsnachweis vorhanden. Bitte beachten Sie den oben
                      angezeigten Stornierungsstatus.
                    </p>
                  ) : null}
                </Section>

                <Section
                  value="contracts"
                  title="Verträge"
                  count={contracts?.total ?? 0}
                >
                  <p className="m-0">
                    {contracts?.signed ?? 0} signiert ·{" "}
                    {contracts?.superseded ?? 0} ersetzt
                  </p>
                  <p className="m-0 mt-1 text-[11.5px] text-muted-foreground">
                    Verträge bleiben als Historie erhalten.
                  </p>
                </Section>
              </Accordion>
            </>
          ) : null}
        </div>

        <div className="grid gap-1.5">
          <label
            htmlFor="tenancy-cancel-reason"
            className="text-[12px] font-medium text-muted-foreground"
          >
            Stornierungsgrund (erforderlich)
          </label>
          <textarea
            id="tenancy-cancel-reason"
            rows={3}
            maxLength={CANCEL_REASON_MAX + 1}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={pending}
            placeholder="Grund für die Stornierung..."
            className="w-full rounded-[8px] border border-input bg-background px-3 py-2 text-[13px] text-foreground outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
          />
          {tooShort ? (
            <p className="m-0 text-[11px] text-muted-foreground">
              Mindestens {CANCEL_REASON_MIN} Zeichen.
            </p>
          ) : null}
          {tooLong ? (
            <p className="m-0 text-[11px] text-muted-foreground">
              Höchstens {CANCEL_REASON_MAX} Zeichen.
            </p>
          ) : null}
          {requiresNoShow ? (
            <label className="mt-1 flex items-start gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={noShowConfirmed}
                onChange={(e) => setNoShowConfirmed(e.target.checked)}
                disabled={pending}
                className="mt-0.5"
              />
              <span>
                Ich bestätige, dass die Mietpartei nicht eingezogen ist.
              </span>
            </label>
          ) : null}
          {submitError ? (
            <p
              role="alert"
              className="m-0 rounded-[8px] border border-destructive/30 bg-destructive/5 px-3 py-2 text-[12.5px] text-destructive"
            >
              Stornierung fehlgeschlagen: {submitError}
            </p>
          ) : null}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Abbrechen</AlertDialogCancel>
          {/* Not AlertDialogAction: that primitive closes on click even when the
              submit must be blocked, and it would close before the API answers —
              a 409 would then look like a successful cancellation. */}
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleConfirm}
            className="inline-flex h-10 items-center justify-center rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:pointer-events-none disabled:opacity-50"
          >
            {pending ? "Storniere …" : "Endgültig stornieren"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
